import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchDonorSession } from '../../api/donorSessionApi';
import { ApiError, toApiError } from '../../api/http';
import {
  DonorInvitationPresentation,
  exchangeDonorInvitation,
  previewDonorInvitation,
  requestPublicDonorResumeLink,
} from '../../api/voiceInvitationsApi';
import { features } from '../../config/features';
import { EmptyState, PillButton, PolicyDisclosure, StateMessage } from '../../components/ui';
import DonorCaptureFlow from './DonorCaptureFlow';
import AnalyticsPreference from '../../components/AnalyticsPreference/AnalyticsPreference';
import { approvedDonorPolicies } from './donorConsentPolicy';
import './VoiceInvitation.css';

type JourneyState =
  | 'loading'
  | 'preview'
  | 'session'
  | 'unavailable'
  | 'expired'
  | 'already_used'
  | 'rate_limited'
  | 'session_ended'
  | 'declined';

function fragmentToken(): string {
  return new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
}

function stateForError(error: ApiError, duringSession: boolean): JourneyState {
  if (error.code === 'rate_limited') return 'rate_limited';
  if (duringSession && error.code === 'unauthorized') return 'session_ended';
  if (error.status === 410) return 'expired';
  if (error.code === 'conflict') return 'already_used';
  return 'unavailable';
}

function removeFragment(): void {
  const cleanUrl = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState(window.history.state, '', cleanUrl);
}

function boundedPresentation(
  presentation: DonorInvitationPresentation,
): DonorInvitationPresentation {
  const effort = presentation.estimated_effort;
  return {
    version: presentation.version,
    state: presentation.state,
    expires_at: presentation.expires_at,
    inviter_display_name: typeof presentation.inviter_display_name === 'string'
      ? presentation.inviter_display_name.slice(0, 100)
      : null,
    listeners: Array.isArray(presentation.listeners)
      ? presentation.listeners.slice(0, 20).map((listener) => ({
        display_name: typeof listener.display_name === 'string'
          ? listener.display_name.slice(0, 100)
          : 'Listener',
      }))
      : [],
    estimated_effort: {
      estimated_minutes: effort.estimated_minutes,
      voice_sample_count: effort.voice_sample_count,
      listener_clip_count_per_listener: effort.listener_clip_count_per_listener,
    },
    policies: presentation.policies,
  };
}

/** "Maya", "Maya and Leo", or "Maya, Leo and Sam". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

interface VoiceInvitationProps {
  enabled?: boolean;
}

const VoiceInvitation: React.FC<VoiceInvitationProps> = ({
  enabled = features.accountlessDonorRoute,
}) => {
  const tokenRef = useRef(fragmentToken());
  const initialRequestStarted = useRef(false);
  const exchangeInFlight = useRef(false);
  const [journeyState, setJourneyState] = useState<JourneyState>(
    enabled ? 'loading' : 'unavailable',
  );
  const [presentation, setPresentation] = useState<DonorInvitationPresentation | null>(null);
  const [busyAction, setBusyAction] = useState<'exchange' | 'resume' | null>(null);
  const [resumeRequested, setResumeRequested] = useState(false);
  const [actionError, setActionError] = useState(false);

  const loadInitialView = useCallback(async () => {
    if (!enabled || initialRequestStarted.current) return;
    initialRequestStarted.current = true;
    const token = tokenRef.current;
    try {
      if (token) {
        const preview = await previewDonorInvitation(token);
        setPresentation(boundedPresentation(preview));
        setJourneyState('preview');
      } else {
        const session = await fetchDonorSession();
        setPresentation(boundedPresentation(session.invitation));
        setJourneyState('session');
      }
    } catch (reason) {
      const error = toApiError(reason);
      setPresentation(null);
      setJourneyState(stateForError(error, !token));
    }
  }, [enabled]);

  useEffect(() => {
    void loadInitialView();
  }, [loadInitialView]);

  const continueWithInvitation = async () => {
    if (exchangeInFlight.current || !tokenRef.current) return;
    exchangeInFlight.current = true;
    setBusyAction('exchange');
    setResumeRequested(false);
    setActionError(false);
    try {
      const session = await exchangeDonorInvitation(tokenRef.current);
      setPresentation(boundedPresentation(session.invitation));
      removeFragment();
      tokenRef.current = '';
      setJourneyState('session');
    } catch (reason) {
      const error = toApiError(reason);
      if (error.code === 'conflict' || error.code === 'rate_limited'
          || error.code === 'not_found' || error.status === 410) {
        setJourneyState(stateForError(error, false));
      } else {
        setActionError(true);
        setJourneyState('preview');
      }
    } finally {
      exchangeInFlight.current = false;
      setBusyAction(null);
    }
  };

  const requestResumeLink = async () => {
    if (busyAction) return;
    setBusyAction('resume');
    try {
      await requestPublicDonorResumeLink(tokenRef.current);
      setResumeRequested(true);
    } catch (reason) {
      const error = toApiError(reason);
      if (error.code === 'unauthorized' && !tokenRef.current) {
        setPresentation(null);
        setJourneyState('session_ended');
      } else if (error.code === 'rate_limited') {
        setJourneyState('rate_limited');
      }
    } finally {
      setBusyAction(null);
    }
  };

  if (journeyState === 'loading') {
    return <StateMessage kind="loading" title="Opening your invitation…" shape="text" className="donor-step" />;
  }

  if (journeyState === 'declined') {
    return (
      <EmptyState
        headingLevel={1}
        className="donor-step"
        title="You declined this invitation"
        message="No recording was started. You can safely close this page."
      />
    );
  }

  if (!presentation || journeyState === 'unavailable' || journeyState === 'expired'
      || journeyState === 'already_used' || journeyState === 'rate_limited'
      || journeyState === 'session_ended') {
    const copy = {
      unavailable: ['This invitation is unavailable', 'The link may be invalid or no longer available.'],
      expired: ['This invitation has expired', 'Ask the person who invited you for a new link.'],
      already_used: ['This link has already been used', 'Open the most recent invitation link to continue.'],
      rate_limited: ['Please wait before trying again', 'Too many requests were made. Try again in a little while.'],
      session_ended: ['Your session has ended', 'Open your most recent invitation link to continue.'],
    }[journeyState === 'preview' || journeyState === 'session'
      ? 'unavailable'
      : journeyState];
    const canRequestResume = Boolean(tokenRef.current)
      && (journeyState === 'expired' || journeyState === 'already_used' || journeyState === 'unavailable');
    return (
      <div className="donor-step" role="status">
        <EmptyState headingLevel={1} title={copy[0]} message={copy[1]} />
        {canRequestResume && (
          <div className="donor-actions">
            <PillButton
              variant="primary"
              busy={busyAction === 'resume'}
              busyLabel="Requesting…"
              disabled={resumeRequested}
              onClick={() => void requestResumeLink()}
            >
              {resumeRequested ? 'Request received' : 'Request a new link'}
            </PillButton>
          </div>
        )}
        {resumeRequested && <p className="donor-note">If a fresh link is available, it will be sent to the address already on the invitation.</p>}
      </div>
    );
  }

  if (!approvedDonorPolicies(presentation.policies)) {
    return (
      <div className="donor-step" role="alert">
        <EmptyState
          headingLevel={1}
          title="This invitation needs an updated consent form"
          message="Recording has not started. Ask the person who invited you for a new link."
        />
      </div>
    );
  }

  if (journeyState === 'session') {
    return (
      <>
      <DonorCaptureFlow
        presentation={presentation}
        onSessionUnavailable={(error) => {
          tokenRef.current = '';
          removeFragment();
          setPresentation(null);
          setJourneyState(stateForError(error, true));
        }}
        onDeclined={() => {
          tokenRef.current = '';
          removeFragment();
          setPresentation(null);
          setJourneyState('declined');
        }}
      />
      <PolicyDisclosure title="Optional product analytics" className="donor-analytics-preference">
        <AnalyticsPreference audience="donor" />
      </PolicyDisclosure>
      </>
    );
  }

  const listenerNames = presentation.listeners.map((listener) => listener.display_name);
  const inviter = presentation.inviter_display_name;
  const listenerText = listenerNames.length > 0 ? joinNames(listenerNames) : 'your loved ones';

  return (
    <div className="donor-step donor-greeting">
      <h1>Share your voice for storytime</h1>
      <p className="donor-lede">
        {inviter ? `${inviter} invited you` : 'You’ve been invited'} to record a few short voice samples and personal messages for {listenerText}.
      </p>
      <dl className="donor-facts" aria-label="What to expect">
        <div><dt>Estimated time</dt><dd>About {presentation.estimated_effort.estimated_minutes} minutes</dd></div>
        <div><dt>Voice samples</dt><dd>{presentation.estimated_effort.voice_sample_count} short passages</dd></div>
      </dl>

      {journeyState === 'preview' ? (
        <>
          {actionError && <p className="donor-error" role="alert">We couldn’t continue just now. Your link is still available, so you can try again.</p>}
          <div className="donor-actions">
            <PillButton
              variant="primary"
              busy={busyAction === 'exchange'}
              busyLabel="Continuing…"
              onClick={() => void continueWithInvitation()}
            >
              Continue
            </PillButton>
          </div>
          <p className="donor-note">Nothing starts until you choose Continue.</p>
        </>
      ) : null}
    </div>
  );
};

export default VoiceInvitation;
